import { useCallback, useEffect, useRef } from 'react';
import { TrackingApi } from 'src/api';
import { PremiumPrice } from 'src/api/internal/schema';
import { BlobPlansTrackingAction, BlobPlansTrackingLocation } from 'src/api/tracking/types';
import { useUserContext } from 'src/context/user';
import { BLOB_TRACKING_DATA } from 'src/apps/LearningSession/BlobSession/tracking';
import { getLanguagePairId } from 'src/utils/language/getLanguagePairId';
import type { PlanTab } from './PlansComponent';

export type BlobPlansDismissCta = 'continue_for_free' | 'back';

type BlobPlansTrackingOptions = {
  location: BlobPlansTrackingLocation;
  /** Read at event time: the plans page takes it from the URL */
  getSource: () => string;
  /** `screen_viewed` fires once, the first time this is true */
  isReady: boolean;
};

/**
 * The blob plans page's own events. Onboarding and /payment/plans both use it, so the
 * two report the same events with the same data and differ only in `location`.
 */
export const useBlobPlansTracking = ({
  location,
  getSource,
  isReady,
}: BlobPlansTrackingOptions) => {
  const { user } = useUserContext();
  const isPro = !!user?.isPro;
  const languagePairId = getLanguagePairId();

  const track = useCallback(
    (action: BlobPlansTrackingAction, trackingData?: Record<string, string>) => {
      void TrackingApi.trackUserAction(action, location, languagePairId, isPro, {
        ...BLOB_TRACKING_DATA,
        source: getSource(),
        ...trackingData,
      });
    },
    [location, languagePairId, isPro, getSource],
  );

  const hasTrackedView = useRef(false);
  useEffect(() => {
    if (!isReady || hasTrackedView.current) return;
    hasTrackedView.current = true;
    track('screen_viewed');
  }, [isReady, track]);

  return {
    trackPlanSelected: (price: PremiumPrice) =>
      track('plan_selected', {
        plan_id: price.key,
        plan: price.paymentPeriod,
        price: String(price.finalPrice),
        sku: price.key,
      }),
    trackTabChange: (tab: PlanTab) => track('clicked_plan_tab', { tab }),
    trackSubscribe: (price: PremiumPrice) =>
      track('clicked_subscribe', {
        plan: price.paymentPeriod,
        price: String(price.finalPrice),
        sku: price.key,
      }),
    trackDismiss: (cta: BlobPlansDismissCta) => {
      if (cta === 'continue_for_free') track('clicked_free_link', { cta });
      track('paywall_dismissed', { cta });
    },
  };
};
